import * as React from 'react';
import { cn } from '../lib/utils.js';

// ---- Spinner ----

export interface SpinnerProps extends React.SVGAttributes<SVGElement> {
  size?: number;
}

function Spinner({ className, size = 24, ...props }: SpinnerProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('animate-spin text-primary-600', className)}
      {...props}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
Spinner.displayName = 'Spinner';

// ---- Skeleton ----

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {}

function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-gray-200', className)}
      {...props}
    />
  );
}
Skeleton.displayName = 'Skeleton';

// ---- PageLoader ----

export interface PageLoaderProps {
  message?: string;
  className?: string;
}

function PageLoader({ message = 'Loading...', className }: PageLoaderProps) {
  return (
    <div
      className={cn(
        'flex min-h-[50vh] flex-col items-center justify-center gap-3',
        className,
      )}
    >
      <Spinner size={32} />
      <p className="text-sm text-gray-500">{message}</p>
    </div>
  );
}
PageLoader.displayName = 'PageLoader';

// ---- TableSkeleton ----

export interface TableSkeletonProps {
  columns?: number;
  rows?: number;
  className?: string;
}

function TableSkeleton({
  columns = 5,
  rows = 8,
  className,
}: TableSkeletonProps) {
  return (
    <div className={cn('w-full overflow-hidden rounded-md border border-gray-200', className)}>
      {/* Header */}
      <div className="flex gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className={cn(
            'flex gap-4 border-b border-gray-100 px-4 py-3',
            rowIdx % 2 === 1 && 'bg-gray-50/50',
          )}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton
              key={colIdx}
              className="h-4 flex-1"
              style={{ width: `${60 + Math.random() * 30}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
TableSkeleton.displayName = 'TableSkeleton';

export { Spinner, Skeleton, PageLoader, TableSkeleton };
